import React from "react";
import { Check } from "lucide-react";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

export const IMPROVE_GOALS = [
  "Overall appearance",
  "Curb appeal",
  "Add color",
  "Replace declining plants",
  "Landscape bed enhancement",
  "Privacy/screening",
  "Modernize the landscape",
  "Turf improvement",
  "Irrigation improvements",
  "Landscape lighting",
  "Property cleaning/pressure washing",
  "Sustainable landscape improvements",
  "Not sure — let GreenFlow recommend",
];

export const IMPROVE_GOALS_V3 = [
  "Improve overall appearance",
  "Improve curb appeal",
  "Add seasonal or year-round color",
  "Replace declining or unsuitable plants",
  "Enhance landscape beds",
  "Add privacy or screening",
  "Modernize the landscape",
  "Improve turf",
  "Improve irrigation",
  "Add landscape lighting",
  "Add mulch",
  "Add decorative stone",
  "Add edging",
  "Add an accent or focal planting",
  "Incorporate Florida-friendly landscaping",
  "Incorporate lower-maintenance landscaping",
  "Incorporate sustainable improvements",
  "Property cleaning or pressure washing",
  "Not sure, let GreenFlow recommend",
];
export const STYLES = [
  "Tropical", "Florida-friendly", "Clean and contemporary", "Traditional", "Colorful",
  "Low-maintenance", "Lush and layered", "Minimal", "Let GreenFlow recommend",
];
export const MAINTENANCE = ["Low", "Moderate", "High", "Not sure"];
export const BUDGETS = [
  "Planning only", "Under $2,500", "$2,500 to $5,000", "$5,000 to $10,000",
  "$10,000 to $20,000", "More than $20,000", "Not sure",
];
export const V3_DEFAULTS = {
  improvement_goals: [],
  landscape_style: [],
  preferred_colors: "",
  dislikes: "",
  maintenance_level: "",
  budget_range: "",
  preserve_areas: "",
  preserve_features: "",
  additional_notes: "",
};

const NOTICED = ["Within the last week", "1-4 weeks ago", "1-3 months ago", "More than 3 months ago", "Not sure"];
const TREND = ["Improving", "Worsening", "Unchanged", "Not sure"];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function ChipGroup({ name, options, value, onChange, multi = false, tone = "blue" }) {
  const list = Array.isArray(value) ? value : [];
  const isOn = (o) => (multi ? list.includes(o) : value === o);
  const toggle = (o) => {
    if (!multi) return onChange(o);
    onChange(isOn(o) ? list.filter((v) => v !== o) : [...list, o]);
  };
  const on = tone === "blue" ? "bg-sky-600 border-sky-600 text-white" : "bg-emerald-600 border-emerald-600 text-white";
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          type="button"
          key={o}
          data-testid={`${name}-option-${slug(o)}`}
          onClick={() => toggle(o)}
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors ${
            isOn(o) ? on : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
          }`}
        >
          {isOn(o) && <Check size={14} />} {o}
        </button>
      ))}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {hint && <div className="text-xs text-slate-500 -mt-0.5">{hint}</div>}
      {children}
    </div>
  );
}

export function DiagnoseQuestions({ value, onChange }) {
  const set = (k) => (v) => onChange({ ...value, [k]: v?.target ? v.target.value : v });
  const area = (k, placeholder) => (
    <Textarea data-testid={`intake-${slug(k)}-input`} rows={3} className="bg-white border-sky-200" placeholder={placeholder} value={value[k]} onChange={set(k)} />
  );
  return (
    <div className="space-y-5">
      <Field label="Describe your concern">{area("concern", "E.g. Leaves on the front hedge are yellowing with brown spots.")}</Field>
      <Field label="When did you first notice the problem?">
        <ChipGroup name="first-noticed" options={NOTICED} value={value.first_noticed} onChange={set("first_noticed")} />
      </Field>
      <Field label="Is the condition improving, worsening or unchanged?">
        <ChipGroup name="trend" options={TREND} value={value.trend} onChange={set("trend")} />
      </Field>
      <Field label="Treatments already attempted" hint="Optional">{area("treatments_tried", "E.g. Fertilized two weeks ago, sprayed fungicide once.")}</Field>
      <Field label="Irrigation / watering information" hint="Optional — if known">{area("irrigation", "E.g. Sprinklers run 3x per week, 15 minutes per zone.")}</Field>
      <Field label="Recent landscape work or environmental changes" hint="Optional — if known">{area("recent_changes", "E.g. New mulch, construction nearby, recent storm or freeze.")}</Field>
    </div>
  );
}

export function ImproveQuestionsV3({ value, onChange }) {
  const set = (k) => (v) => onChange({ ...value, [k]: v?.target ? v.target.value : v });
  const text = (k, placeholder, rows = 2) => (
    <Textarea data-testid={`intake-${slug(k)}-input`} rows={rows} className="bg-white border-emerald-200" placeholder={placeholder} value={value[k] || ""} onChange={set(k)} />
  );
  return (
    <div className="space-y-5" data-testid="improve-v3-questions">
      <Field label="What would you like to improve?" hint="Select all that apply">
        <ChipGroup name="improve-goal" multi tone="green" options={IMPROVE_GOALS_V3} value={value.improvement_goals} onChange={set("improvement_goals")} />
      </Field>
      <Field label="Preferred landscape style" hint="Select one or more">
        <ChipGroup name="style" multi tone="green" options={STYLES} value={value.landscape_style} onChange={set("landscape_style")} />
      </Field>
      <Field label="Preferred colors" hint="Optional">{text("preferred_colors", "E.g. Whites and soft pinks, lots of deep green.")}</Field>
      <Field label="Plants or colors you dislike" hint="Optional">{text("dislikes", "E.g. No orange, no thorny plants.")}</Field>
      <Field label="Desired maintenance level">
        <ChipGroup name="maintenance" tone="green" options={MAINTENANCE} value={value.maintenance_level} onChange={set("maintenance_level")} />
      </Field>
      <Field label="Approximate budget range">
        <ChipGroup name="budget" tone="green" options={BUDGETS} value={value.budget_range} onChange={set("budget_range")} />
        <div data-testid="budget-disclaimer" className="text-xs text-slate-500 mt-1.5">
          Budget selection is for concept guidance only. It is not a GreenFlow estimate or quotation.
        </div>
      </Field>
      <Field label="Areas that must remain unchanged" hint="Optional">{text("preserve_areas", "E.g. Keep the driveway and the side yard exactly as is.")}</Field>
      <Field label="Existing plants or features to preserve" hint="Optional">{text("preserve_features", "E.g. The large oak tree and the palms by the entry.")}</Field>
      <Field label="Additional comments" hint="Optional">{text("additional_notes", "Anything else GreenFlow should know?", 3)}</Field>
    </div>
  );
}

export function ImproveQuestions({ value, onChange }) {
  return (
    <div className="space-y-5">
      <Field label="What would you like to improve?" hint="Select all that apply">
        <ChipGroup name="improve-goal" multi tone="green" options={IMPROVE_GOALS} value={value.improvement_goals} onChange={(v) => onChange({ ...value, improvement_goals: v })} />
      </Field>
      <Field label="Anything else we should know?" hint="Optional">
        <Textarea data-testid="intake-improve-notes-input" rows={3} className="bg-white border-emerald-200" placeholder="E.g. We love low-maintenance native plants and want more evening lighting." value={value.additional_notes} onChange={(e) => onChange({ ...value, additional_notes: e.target.value })} />
      </Field>
    </div>
  );
}
